import React, {useCallback, useMemo} from 'react';

import {useAppRates} from '@/context/AppRatesContext';
import {useCountryList} from '@/context/CountryListContext';
import {AfriexCurrencyCode} from '@/types';
import {getUserCurrencyFromCountry} from '@/utils/countries';
import {convertCurrency} from '@/utils/currencyConverter';

import type {UserTransactionLimitsProps} from './types';

const TransactionLimits: React.FC<UserTransactionLimitsProps> = ({
  isVerified,
  perTransactionLimit,
  dailyLimit,
  country,
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList, loading: countryListLoading} = countryListState;
  const {state: appRatesState} = useAppRates();
  const {appRates, loading: appRatesLoading} = appRatesState;

  const userCurrency = getUserCurrencyFromCountry(countryList, country);

  const getConvertedLimit = useCallback(
    (amount: number) => {
      const converted = convertCurrency({
        from: AfriexCurrencyCode.USD,
        to: userCurrency,
        value: amount,
        rates: appRates,
      });
      return converted
        ? `${converted.toLocaleString()} ${userCurrency}`
        : 'No Data Available';
    },
    [appRates, userCurrency],
  );

  // Derived purely from the props/context values below; nothing else writes
  // `limits`, so this reads during render instead of round-tripping through
  // an effect + separate `limits` state.
  const limits = useMemo(():
    | {newUserLimit: string; perTransactionLimit: string; dailyLimit: string}
    | undefined => {
    if (
      !perTransactionLimit ||
      !dailyLimit ||
      appRatesLoading ||
      countryListLoading
    ) {
      return undefined;
    }

    return {
      newUserLimit: getConvertedLimit(100),
      perTransactionLimit: getConvertedLimit(perTransactionLimit),
      dailyLimit: getConvertedLimit(dailyLimit),
    };
  }, [
    dailyLimit,
    getConvertedLimit,
    perTransactionLimit,
    appRatesLoading,
    countryListLoading,
  ]);

  return (
    <div className="px-3 pt-3 mt-8">
      <h3 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
        {'TRANSACTION LIMITS'}
      </h3>
      <hr className="mb-5 dark:border-dark-borderMid" />

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-200">
          Send / Withdraw Limits
        </h3>

        <div className="overflow-x-auto">
          {!isVerified ? (
            <div className="mmb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                New User Limit
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                {limits?.newUserLimit || 'No Data Available'}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
                <p className="text-slate-500 text-sm dark:text-slate-100">
                  Per Transaction
                </p>
                <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                  {limits?.perTransactionLimit || 'No Data Available'}
                </p>
              </div>
              <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
                <p className="text-slate-500 text-sm dark:text-slate-100">
                  Daily
                </p>
                <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                  {limits?.dailyLimit || 'No Data Available'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionLimits;
